import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, mergeProps as _mergeProps, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "d-flex justify-space-between align-center px-4 py-2 bg-indigo-lighten-2" }
const _hoisted_2 = {
  class: "d-flex",
  style: {"gap":"8px"}
}
const _hoisted_3 = {
  key: 0,
  class: "d-flex justify-center my-12 text-h6 text-grey"
}
const _hoisted_4 = {
  key: 1,
  class: "d-flex justify-center my-12 text-h6 text-grey"
}
const _hoisted_5 = { class: "mr-2 font-weight-medium" }
const _hoisted_6 = {
  key: 0,
  class: "ml-1 text-caption"
}
const _hoisted_7 = { class: "d-flex justify-end px-4 pt-2" }
const _hoisted_8 = {
  key: 0,
  class: "d-flex justify-center my-8 text-h6 text-grey"
}
const _hoisted_9 = {
  key: 1,
  class: "d-flex justify-center my-8 text-h6 text-grey"
}
const _hoisted_10 = { class: "d-flex justify-space-between align-center" }
const _hoisted_11 = { class: "flex-1" }
const _hoisted_12 = { class: "font-weight-medium text-body-2" }
const _hoisted_13 = { class: "mt-1" }
const _hoisted_14 = {
  key: 0,
  class: "text-red text-caption mt-1"
}

import { onMounted, reactive, ref, watch, computed } from 'vue'
import { useApi } from '@/api/handler'
import { ApiMap } from '@/api/type'
import { game, log } from '@/api'
import DynamicForm from '@/components/DynamicForm.vue'

type TodoTile = ApiMap['/game/special/list']['resp'][number]['unTriggeredTiles'][number]
type TodoReward = ApiMap['/game/reward/queue']['resp']['rewards'][number]

export default /*@__PURE__*/_defineComponent({
  __name: 'SpecialCard',
  props: {
    data: {}
  },
  emits: ["update", "startIssueReward"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emits = __emit

const GameStatus = ref<ApiMap['/game/status/:id']['resp'] | null>(null)
const eventData = ref<ApiMap['/game/special/list']['resp'] | null>(null)
const rewardQueue = ref<ApiMap['/game/reward/queue']['resp'] | null>(null)
const teamLogs = ref<ApiMap['/log/team/list']['resp'] | null>(null)

const activeTab = ref<'todo' | 'log'>('todo')

// 事件结算表单
const submitObject = reactive<{
  type: number
  showOverlay: boolean
  data: Record<string, unknown>
}>({
  type: 0,
  showOverlay: false,
  data: {}
})

// 新增优秀者奖励：DynamicForm 数据模型（与事件结算同款）
const rewardObject = reactive<{
  showOverlay: boolean
  data: Record<string, unknown>
}>({
  showOverlay: false,
  data: {}
})

// 编辑暂存奖励：DynamicForm 数据模型；id 单独保存（不放进 model，避免被 excluded-keys 字符串匹配误伤）
const editingRewardId = ref<string>('')
const editRewardObject = reactive<{
  showOverlay: boolean
  data: Record<string, unknown>
}>({
  showOverlay: false,
  data: {}
})

const issuingReward = ref<{ id: number; stepCount: number; teamId: number } | null>(null)

// 进入下一轮确认弹窗
const nextRoundConfirm = ref(false)
const confirmNextRound = () => { nextRoundConfirm.value = true }
const doNextRound = () => {
  if (!GameStatus.value) return
  useApi({
    api: game.NextRound(GameStatus.value.id),
    onSuccess: () => {
      nextRoundConfirm.value = false
      emits('update')
    },
    tip: '已进入下一轮'
  })
}

// 撤销二次确认
const revokeConfirm = reactive({
  show: false,
  logId: 0
})

// 可撤销条目数量（用于 tab 上的角标）
const revocableCount = computed(() => {
  if (!teamLogs.value || !teamLogs.value.list) return 0
  return teamLogs.value.list.filter((l) => l.comment !== 'REVOKED' && l.comment !== null).length
})

// 待办事项合并视图：把暂存的优秀者奖励按 teamId 合并到对应组的待办行尾部
const todoList = computed<Array<{ teamId: number; tiles: TodoTile[]; rewards: TodoReward[] }>>(() => {
  const map = new Map<number, { teamId: number; tiles: TodoTile[]; rewards: TodoReward[] }>()
  if (eventData.value) {
    for (const t of eventData.value) {
      map.set(t.teamId, {
        teamId: t.teamId,
        tiles: t.unTriggeredTiles ?? [],
        rewards: []
      })
    }
  }
  if (rewardQueue.value && rewardQueue.value.rewards) {
    for (const r of rewardQueue.value.rewards) {
      let entry = map.get(r.teamId)
      if (!entry) {
        entry = { teamId: r.teamId, tiles: [], rewards: [] }
        map.set(r.teamId, entry)
      }
      entry.rewards.push(r)
    }
  }
  // 有待办格子事件的组排在前面，纯奖励的组排在末尾，组内按组号升序
  return Array.from(map.values()).sort((a, b) => {
    const aHasTile = a.tiles.length > 0 ? 0 : 1
    const bHasTile = b.tiles.length > 0 ? 0 : 1
    if (aHasTile !== bHasTile) return aHasTile - bHasTile
    return a.teamId - b.teamId
  })
})

// ---------- 撤销日志解析 ----------
function parseEventMeta(comment: string | null): Record<string, any> | null {
  if (!comment || comment === 'REVOKED') return null
  try {
    const data = JSON.parse(comment)
    if (Array.isArray(data)) {
      // 旧格式：纯快照数组，从中提取参与组号
      return { oldFormat: true, teamIds: data.map((t: any) => t.teamId) }
    }
    // 新格式：含 snap + 元数据的对象
    return data.snap !== undefined ? data : null
  } catch {
    return null
  }
}

function parseEventTitle(comment: string | null): string {
  const meta = parseEventMeta(comment)
  if (!meta) return '事件结算'
  if (meta.oldFormat) {
    const teams = (meta.teamIds as number[]).map(id => `第${id}组`).join(' VS ')
    return `事件结算：${teams}`
  }
  if (meta.eventType === 11) {
    return `决斗要塞：第${meta.attackerId}组(攻) VS 第${meta.defenderId}组(守)`
  }
  if (meta.eventType === 10) {
    const allIds = meta.winnerId
      ? [meta.winnerId, ...((meta.loserIds as number[]) ?? [])]
      : ((meta.loserIds as number[]) ?? [])
    const teams = allIds.map(id => `第${id}组`).join(' VS ')
    return `盲盒秘境：${teams}`
  }
  // 奖励发放（eventType=12）已废弃显示，业务上不可撤销
  return '事件结算'
}

function parseEventChips(comment: string | null): Array<{ label: string; color: string }> {
  const meta = parseEventMeta(comment)
  if (!meta) return []

  if (meta.oldFormat) {
    return (meta.teamIds as number[]).map((id: number) => ({
      label: `第${id}组 参与`,
      color: 'primary'
    }))
  }

  const chips: Array<{ label: string; color: string }> = []

  if (meta.eventType === 11) {
    const winGain = meta.tileChange ?? 0
    if (meta.winnerId) chips.push({ label: `第${meta.winnerId}组 胜 +${winGain}格`, color: 'success' })
    if (meta.loserId) chips.push({ label: `第${meta.loserId}组 败 -${winGain}格`, color: 'error' })
  } else if (meta.eventType === 10) {
    if (meta.winnerId) chips.push({ label: `第${meta.winnerId}组 胜 +${meta.winnerGain ?? 0}格`, color: 'success' })
    const loserIds: number[] = meta.loserIds ?? []
    const loserChange: Record<string, number> = meta.loserTileChange ?? {}
    loserIds.forEach((lid: number) => {
      chips.push({ label: `第${lid}组 败 -${loserChange[String(lid)] ?? 0}格`, color: 'error' })
    })
  }
  return chips
}

// ---------- 生命周期 ----------
watch(() => props.data, (newVal) => {
  GameStatus.value = newVal ?? null
  useSpecialList()
  loadRewardQueue()
  loadTeamLogs()
})

onMounted(() => {
  if (props.data) GameStatus.value = props.data
  useSpecialList()
  loadRewardQueue()
  loadTeamLogs()
})

// 阶段从非结算切回结算外时，自动回到待办 tab
watch(
  () => GameStatus.value?.chessPhase,
  (phase) => {
    if (phase !== 3 && activeTab.value !== 'todo') {
      activeTab.value = 'todo'
    }
  }
)

// ---------- 待办事项 / 事件结算 ----------
const useSpecialList = () => {
  if (!GameStatus.value) return
  if (GameStatus.value.chessPhase === 3) {
    // 结算阶段：调用原接口（会在事件全清空时自动推进轮次）
    useApi({
      api: game.SpecialList(GameStatus.value.id),
      onSuccess: (resp) => {
        eventData.value = resp.data as ApiMap['/game/special/list']['resp']
      }
    })
  } else {
    // 非结算阶段：只读 preview，不触发轮次推进，用于显示奖励触发的特殊格子
    useApi({
      api: game.SpecialListPreview(GameStatus.value.id),
      onSuccess: (resp) => {
        eventData.value = resp.data as ApiMap['/game/special/list']['resp']
      }
    })
  }
}

function handleClick(index: number, subIndex: number) {
  if (!eventData.value) return
  const tile = eventData.value[index].unTriggeredTiles[subIndex]
  submitObject.type = tile.tileType
  let v_data: Record<string, unknown> | null = null
  if (tile.tileType === 1) {
    v_data = {
      teamId: eventData.value[index].teamId,
      involvedTeamOne: 0,
      involvedTeamTwo: 0,
      winnerTeamId: 0,
      tileId: tile.tileId,
      eventType: tile.eventType
    }
  } else if (tile.tileType === 2 || tile.tileType === 3) {
    v_data = {
      attackerTeamId: eventData.value[index].teamId,
      defenderTeamId: 0,
      winnerTeamId: 0,
      gameType: tile.eventType,
      tileId: tile.tileId
    }
  }
  if (!v_data) return
  submitObject.data = v_data
  submitObject.showOverlay = true
}

// 待办合并视图直接拿 (teamId, tile) 触发结算表单，等价于旧 handleClick
function handleClickTile(teamId: number, tile: TodoTile) {
  submitObject.type = tile.tileType
  let v_data: Record<string, unknown> | null = null
  if (tile.tileType === 1) {
    v_data = {
      teamId,
      involvedTeamOne: 0,
      involvedTeamTwo: 0,
      winnerTeamId: 0,
      tileId: tile.tileId,
      eventType: tile.eventType
    }
  } else if (tile.tileType === 2 || tile.tileType === 3) {
    v_data = {
      attackerTeamId: teamId,
      defenderTeamId: 0,
      winnerTeamId: 0,
      gameType: tile.eventType,
      tileId: tile.tileId
    }
  }
  if (!v_data) return
  submitObject.data = v_data
  submitObject.showOverlay = true
}

const useSubmitBlindBox = (data: unknown) => {
  if (!GameStatus.value) return
  const transData = data as {
    teamId: number
    involvedTeamOne: number
    involvedTeamTwo: number
    winnerTeamId: number
    tileId: number
    eventType: number
  }
  useApi({
    api: game.SubmitBlindBox({
      teamId: transData.teamId,
      winnerTeamId: Number(transData.winnerTeamId),
      eventType: transData.eventType,
      tileId: transData.tileId,
      gameId: GameStatus.value.id,
      involvedTeamIds: [
        Number(transData.teamId),
        Number(transData.involvedTeamOne),
        Number(transData.involvedTeamTwo)
      ]
    }),
    onSuccess: () => {
      emits('update')
      submitObject.showOverlay = false
      loadTeamLogs()
    },
    tip: '上传成功'
  })
}

const useSubmitFortress = (data: unknown) => {
  if (!GameStatus.value) return
  const transData = data as {
    attackerTeamId: number
    defenderTeamId: number
    winnerTeamId: number
    gameType: number
    tileId: number
  }
  useApi({
    api: game.SubmitFortress({
      attackerTeamId: Number(transData.attackerTeamId),
      defenderTeamId: Number(transData.defenderTeamId),
      winnerTeamId: Number(transData.winnerTeamId),
      gameType: transData.gameType,
      tileId: transData.tileId,
      gameId: GameStatus.value.id
    }),
    onSuccess: () => {
      emits('update')
      loadTeamLogs()
    },
    Finally: () => {
      submitObject.showOverlay = false
    },
    tip: '上传成功'
  })
}

// ---------- 撤销 ----------
const confirmRevoke = (logId: number) => {
  revokeConfirm.logId = logId
  revokeConfirm.show = true
}

const doRevoke = () => {
  if (!GameStatus.value || !revokeConfirm.logId) return
  useApi({
    api: game.RevokeEvent({
      gameId: GameStatus.value.id,
      logId: revokeConfirm.logId
    }),
    onSuccess: () => {
      revokeConfirm.show = false
      revokeConfirm.logId = 0
      loadTeamLogs()
      useSpecialList()
      emits('update')
    },
    tip: '撤销成功，棋盘状态已回滚'
  })
}

// ---------- 事件日志 ----------
const loadTeamLogs = () => {
  if (!GameStatus.value) return
  useApi({
    api: log.TeamLog({
      gameId: GameStatus.value.id,
      teamId: 0,
      pageNum: 1,
      pageSize: 50,
      phase: 1,
      round: GameStatus.value.chessRound,
      revocableOnly: true
    }),
    onSuccess: (resp) => {
      teamLogs.value = resp.data as ApiMap['/log/team/list']['resp']
    }
  })
}

// ---------- 优秀者奖励：暂存 / 修改 / 删除 / 发放 ----------
const openRewardModal = () => {
  rewardObject.data = {
    teamId: 0,
    stepCount: 1,
    reason: ''
  }
  rewardObject.showOverlay = true
}

const useSubmitReward = (data: unknown) => {
  if (!GameStatus.value) return
  const d = data as { teamId: number; stepCount: number; reason: string }
  if (!d.teamId) return
  useApi({
    api: game.TempReward({
      gameId: GameStatus.value.id,
      teamId: Number(d.teamId),
      stepCount: Number(d.stepCount),
      reason: d.reason ?? ''
    }),
    onSuccess: () => {
      rewardObject.showOverlay = false
      loadRewardQueue()
      emits('update')
      activeTab.value = 'todo'
    },
    tip: '奖励已暂存，已追加到对应组的待办事项尾部'
  })
}

const openEditRewardModal = (
  reward: ApiMap['/game/reward/queue']['resp']['rewards'][0]
) => {
  editingRewardId.value = reward.id
  // 后端 RewardUpdateReq 只接受 stepCount + reason，不允许换组；只 expose 这两个字段
  editRewardObject.data = {
    stepCount: reward.stepCount,
    reason: reward.reason ?? ''
  }
  editRewardObject.showOverlay = true
}

const useSubmitEditReward = (data: unknown) => {
  if (!editingRewardId.value || editingRewardId.value === '0') return
  const d = data as { stepCount: number; reason: string }
  useApi({
    api: game.UpdateReward({
      id: editingRewardId.value,
      stepCount: Number(d.stepCount),
      reason: d.reason ?? ''
    }),
    onSuccess: () => {
      editRewardObject.showOverlay = false
      editingRewardId.value = 0
      loadRewardQueue()
      emits('update')
    },
    tip: '奖励修改成功'
  })
}

// ---------- 发放单条奖励：改为让用户在棋盘上点选格子 ----------
const openIssueRewardModal = (
  reward: ApiMap['/game/reward/queue']['resp']['rewards'][0]
) => {
  emits('startIssueReward', {
    rewardId: reward.id,
    teamId: reward.teamId,
    stepCount: reward.stepCount
  })
}

const loadRewardQueue = () => {
  if (!GameStatus.value) return
  useApi({
    api: game.GetRewardQueue(GameStatus.value.id),
    onSuccess: (resp) => {
      rewardQueue.value = resp.data as ApiMap['/game/reward/queue']['resp']
    }
  })
}

const deleteRewardItem = (id: number) => {
  useApi({
    api: game.DeleteReward(id),
    onSuccess: () => {
      loadRewardQueue()
      emits('update')
    },
    tip: '奖励已删除'
  })
}


return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_v_tab = _resolveComponent("v-tab")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_tabs = _resolveComponent("v-tabs")!
  const _component_v_list_item_title = _resolveComponent("v-list-item-title")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_list = _resolveComponent("v-list")!
  const _component_v_menu = _resolveComponent("v-menu")!
  const _component_v_window_item = _resolveComponent("v-window-item")!
  const _component_v_window = _resolveComponent("v-window")!
  const _component_v_overlay = _resolveComponent("v-overlay")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_v_card, { class: "my-4" }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _cache[16] || (_cache[16] = _createElementVNode("span", { class: "text-h6" }, "各组事务", -1)),
          _createElementVNode("div", _hoisted_2, [
            (GameStatus.value && GameStatus.value.chessPhase === 3)
              ? (_openBlock(), _createBlock(_component_v_btn, {
                  key: 0,
                  size: "small",
                  color: "success",
                  onClick: openRewardModal
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_icon, {
                      icon: "mdi-gift",
                      class: "mr-1"
                    }),
                    _cache[14] || (_cache[14] = _createTextVNode(" 优秀者奖励 "))
                  ]),
                  _: 1,
                  __: [14]
                }))
              : _createCommentVNode("", true),
            (GameStatus.value && GameStatus.value.chessPhase === 3 && (eventData.value?.length ?? 0) === 0)
              ? (_openBlock(), _createBlock(_component_v_btn, {
                  key: 1,
                  size: "small",
                  color: "warning",
                  onClick: confirmNextRound
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_icon, {
                      icon: "mdi-arrow-right-bold",
                      class: "mr-1"
                    }),
                    _cache[15] || (_cache[15] = _createTextVNode(" 进入下一轮 "))
                  ]),
                  _: 1,
                  __: [15]
                }))
              : _createCommentVNode("", true)
          ])
        ]),
        _createVNode(_component_v_dialog, {
          modelValue: nextRoundConfirm.value,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((nextRoundConfirm).value = $event)),
          "max-width": "420"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_card, null, {
              default: _withCtx(() => [
                _createVNode(_component_v_card_title, { class: "text-h6" }, {
                  default: _withCtx(() => _cache[17] || (_cache[17] = [
                    _createTextVNode("确认进入下一轮？")
                  ])),
                  _: 1,
                  __: [17]
                }),
                _createVNode(_component_v_card_text, null, {
                  default: _withCtx(() => _cache[18] || (_cache[18] = [
                    _createTextVNode(" 进入下一轮后，本轮所有事件将不可撤销。"),
                    _createElementVNode("br", null, null, -1),
                    _createElementVNode("span", { class: "text-caption text-grey" }, "请确认所有结算无误后再继续。", -1)
                  ])),
                  _: 1,
                  __: [18]
                }),
                _createVNode(_component_v_card_actions, null, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_spacer),
                    _createVNode(_component_v_btn, {
                      color: "grey",
                      variant: "text",
                      onClick: _cache[0] || (_cache[0] = ($event: any) => (nextRoundConfirm.value = false))
                    }, {
                      default: _withCtx(() => _cache[19] || (_cache[19] = [
                        _createTextVNode("取消")
                      ])),
                      _: 1,
                      __: [19]
                    }),
                    _createVNode(_component_v_btn, {
                      color: "warning",
                      variant: "elevated",
                      onClick: doNextRound
                    }, {
                      default: _withCtx(() => _cache[20] || (_cache[20] = [
                        _createTextVNode("确认进入")
                      ])),
                      _: 1,
                      __: [20]
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["modelValue"]),
        _createVNode(_component_v_tabs, {
          modelValue: activeTab.value,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((activeTab).value = $event)),
          color: "primary",
          grow: "",
          density: "comfortable"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_tab, { value: "todo" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_icon, {
                  icon: "mdi-clipboard-list",
                  class: "mr-1"
                }),
                _cache[21] || (_cache[21] = _createTextVNode(" 待办事项 "))
              ]),
              _: 1,
              __: [21]
            }),
            (GameStatus.value && GameStatus.value.stage === 1)
              ? (_openBlock(), _createBlock(_component_v_tab, {
                  key: 0,
                  value: "log"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_icon, {
                      icon: "mdi-history",
                      class: "mr-1"
                    }),
                    _cache[22] || (_cache[22] = _createTextVNode(" 可撤销日志 ")),
                    (revocableCount.value > 0)
                      ? (_openBlock(), _createBlock(_component_v_chip, {
                          key: 0,
                          size: "x-small",
                          color: "orange",
                          class: "ml-2"
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(revocableCount.value), 1)
                          ]),
                          _: 1
                        }))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1,
                  __: [22]
                }))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["modelValue"]),
        _createVNode(_component_v_window, {
          modelValue: activeTab.value,
          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((activeTab).value = $event))
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_window_item, { value: "todo" }, {
              default: _withCtx(() => [
                (!eventData.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_3, "结算阶段展示"))
                  : (todoList.value.length === 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_4, "暂无待办事项"))
                    : (_openBlock(), _createBlock(_component_v_list, {
                        key: 2,
                        class: "pa-2 overflow-y-auto",
                        "max-height": "260px"
                      }, {
                        default: _withCtx(() => [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(todoList.value, (row) => {
                            return (_openBlock(), _createBlock(_component_v_list_item, {
                              class: "d-flex",
                              key: row.teamId
                            }, {
                              default: _withCtx(() => [
                                _createElementVNode("span", _hoisted_5, "第 " + _toDisplayString(row.teamId) + " 组：", 1),
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(row.tiles, (e, subIdx) => {
                                  return (_openBlock(), _createBlock(_component_v_chip, {
                                    color: e.tileType === 1 ? 'orange' : 'blue',
                                    key: `tile-${subIdx}`,
                                    class: "mr-2 mb-1",
                                    onClick: ($event: any) => (handleClickTile(row.teamId, e))
                                  }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(e.eventName), 1)
                                    ]),
                                    _: 2
                                  }, 1032, ["color", "onClick"]))
                                }), 128)),
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(row.rewards, (r) => {
                                  return (_openBlock(), _createBlock(_component_v_menu, {
                                    key: `reward-${r.id}`
                                  }, {
                                    activator: _withCtx(({ props: menuProps }) => [
                                      _createVNode(_component_v_chip, _mergeProps({ ref_for: true }, menuProps, {
                                        color: "success",
                                        variant: "elevated",
                                        class: "mr-2 mb-1"
                                      }), {
                                        default: _withCtx(() => [
                                          _createVNode(_component_v_icon, {
                                            icon: "mdi-gift",
                                            start: ""
                                          }),
                                          _createTextVNode(" 优秀者 +" + _toDisplayString(r.stepCount) + " 步 ", 1),
                                          (r.reason)
                                            ? (_openBlock(), _createElementBlock("span", _hoisted_6, "（" + _toDisplayString(r.reason) + "）", 1))
                                            : _createCommentVNode("", true)
                                        ]),
                                        _: 2
                                      }, 1040)
                                    ]),
                                    default: _withCtx(() => [
                                      _createVNode(_component_v_list, { density: "compact" }, {
                                        default: _withCtx(() => [
                                          _createVNode(_component_v_list_item, {
                                            onClick: ($event: any) => (openIssueRewardModal(r))
                                          }, {
                                            prepend: _withCtx(() => [
                                              _createVNode(_component_v_icon, {
                                                icon: "mdi-send",
                                                color: "success"
                                              })
                                            ]),
                                            default: _withCtx(() => [
                                              _createVNode(_component_v_list_item_title, null, {
                                                default: _withCtx(() => _cache[23] || (_cache[23] = [
                                                  _createTextVNode("发放（选择落点格子）")
                                                ])),
                                                _: 1,
                                                __: [23]
                                              })
                                            ]),
                                            _: 2
                                          }, 1032, ["onClick"]),
                                          _createVNode(_component_v_list_item, {
                                            onClick: ($event: any) => (openEditRewardModal(r))
                                          }, {
                                            prepend: _withCtx(() => [
                                              _createVNode(_component_v_icon, {
                                                icon: "mdi-pencil",
                                                color: "orange"
                                              })
                                            ]),
                                            default: _withCtx(() => [
                                              _createVNode(_component_v_list_item_title, null, {
                                                default: _withCtx(() => _cache[24] || (_cache[24] = [
                                                  _createTextVNode("编辑步数 / 原因")
                                                ])),
                                                _: 1,
                                                __: [24]
                                              })
                                            ]),
                                            _: 2
                                          }, 1032, ["onClick"]),
                                          _createVNode(_component_v_list_item, {
                                            onClick: ($event: any) => (deleteRewardItem(r.id))
                                          }, {
                                            prepend: _withCtx(() => [
                                              _createVNode(_component_v_icon, {
                                                icon: "mdi-delete",
                                                color: "error"
                                              })
                                            ]),
                                            default: _withCtx(() => [
                                              _createVNode(_component_v_list_item_title, null, {
                                                default: _withCtx(() => _cache[25] || (_cache[25] = [
                                                  _createTextVNode("删除")
                                                ])),
                                                _: 1,
                                                __: [25]
                                              })
                                            ]),
                                            _: 2
                                          }, 1032, ["onClick"])
                                        ]),
                                        _: 2
                                      }, 1024)
                                    ]),
                                    _: 2
                                  }, 1024))
                                }), 128))
                              ]),
                              _: 2
                            }, 1024))
                          }), 128))
                        ]),
                        _: 1
                      }))
              ]),
              _: 1
            }),
            _createVNode(_component_v_window_item, { value: "log" }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_7, [
                  _createVNode(_component_v_btn, {
                    size: "small",
                    color: "primary",
                    variant: "tonal",
                    onClick: loadTeamLogs
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_icon, {
                        icon: "mdi-refresh",
                        class: "mr-1"
                      }),
                      _cache[26] || (_cache[26] = _createTextVNode(" 刷新 "))
                    ]),
                    _: 1,
                    __: [26]
                  })
                ]),
                (!teamLogs.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_8, "加载中..."))
                  : (!teamLogs.value.list || teamLogs.value.list.length === 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_9, " 暂无事件记录 "))
                    : (_openBlock(), _createBlock(_component_v_list, {
                        key: 2,
                        class: "pa-2 overflow-y-auto",
                        "max-height": "280px"
                      }, {
                        default: _withCtx(() => [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(teamLogs.value.list, (logItem) => {
                            return (_openBlock(), _createBlock(_component_v_list_item, {
                              key: logItem.id,
                              class: _normalizeClass([{ 'opacity-50': logItem.comment === 'REVOKED' }, "mb-1"])
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_v_card, {
                                  variant: "outlined",
                                  class: "pa-2 w-100",
                                  color: logItem.comment === 'REVOKED' ? 'grey' : 'default'
                                }, {
                                  default: _withCtx(() => [
                                    _createElementVNode("div", _hoisted_10, [
                                      _createElementVNode("div", _hoisted_11, [
                                        _createElementVNode("div", _hoisted_12, _toDisplayString(parseEventTitle(logItem.comment)), 1),
                                        _createElementVNode("div", _hoisted_13, [
                                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(parseEventChips(logItem.comment), (chip, idx) => {
                                            return (_openBlock(), _createBlock(_component_v_chip, {
                                              key: idx,
                                              color: chip.color,
                                              size: "x-small",
                                              class: "mr-1 mb-1",
                                              variant: "tonal"
                                            }, {
                                              default: _withCtx(() => [
                                                _createTextVNode(_toDisplayString(chip.label), 1)
                                              ]),
                                              _: 2
                                            }, 1032, ["color"]))
                                          }), 128))
                                        ]),
                                        (logItem.comment === 'REVOKED')
                                          ? (_openBlock(), _createElementBlock("div", _hoisted_14, "已撤销，格子已还原"))
                                          : _createCommentVNode("", true)
                                      ]),
                                      (logItem.comment !== 'REVOKED')
                                        ? (_openBlock(), _createBlock(_component_v_btn, {
                                            key: 0,
                                            size: "small",
                                            color: "error",
                                            variant: "tonal",
                                            class: "ml-2 flex-shrink-0",
                                            onClick: ($event: any) => (confirmRevoke(logItem.id))
                                          }, {
                                            default: _withCtx(() => [
                                              _createVNode(_component_v_icon, {
                                                icon: "mdi-undo",
                                                class: "mr-1"
                                              }),
                                              _cache[27] || (_cache[27] = _createTextVNode(" 撤销 "))
                                            ]),
                                            _: 2,
                                            __: [27]
                                          }, 1032, ["onClick"]))
                                        : _createCommentVNode("", true)
                                    ])
                                  ]),
                                  _: 2
                                }, 1032, ["color"])
                              ]),
                              _: 2
                            }, 1032, ["class"]))
                          }), 128))
                        ]),
                        _: 1
                      }))
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["modelValue"])
      ]),
      _: 1
    }),
    _createVNode(_component_v_overlay, {
      modelValue: submitObject.showOverlay,
      "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((submitObject.showOverlay) = $event)),
      class: "align-center justify-center"
    }, {
      default: _withCtx(() => [
        (submitObject.type === 1)
          ? (_openBlock(), _createBlock(DynamicForm, {
              key: 0,
              headers: ['小组号', '参加队伍二', '参加队伍三', '获胜方编号', 'ID', '事件类型'],
              "excluded-keys": ['Type', 'tileId'],
              model: submitObject.data,
              onSubmit: useSubmitBlindBox,
              onClose: _cache[4] || (_cache[4] = ($event: any) => (submitObject.showOverlay = false))
            }, null, 8, ["model"]))
          : _createCommentVNode("", true),
        (submitObject.type === 2)
          ? (_openBlock(), _createBlock(DynamicForm, {
              key: 1,
              headers: ['进攻方', '防守方', '获胜方编号', '事件类型', 'ID'],
              "excluded-keys": ['Type', 'tileId'],
              model: submitObject.data,
              onSubmit: useSubmitFortress,
              onClose: _cache[5] || (_cache[5] = ($event: any) => (submitObject.showOverlay = false))
            }, null, 8, ["model"]))
          : _createCommentVNode("", true),
        (submitObject.type === 3)
          ? (_openBlock(), _createBlock(DynamicForm, {
              key: 2,
              headers: ['进攻方', '防守方', '获胜方编号', '事件类型', 'ID'],
              "excluded-keys": ['Type', 'tileId'],
              model: submitObject.data,
              onSubmit: useSubmitFortress,
              onClose: _cache[6] || (_cache[6] = ($event: any) => (submitObject.showOverlay = false))
            }, null, 8, ["model"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(_component_v_overlay, {
      modelValue: rewardObject.showOverlay,
      "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((rewardObject.showOverlay) = $event)),
      class: "align-center justify-center"
    }, {
      default: _withCtx(() => [
        _createVNode(DynamicForm, {
          title: "请填写优秀者奖励",
          headers: ['获奖小组号', '奖励步数', '奖励原因'],
          model: rewardObject.data,
          onSubmit: useSubmitReward,
          onClose: _cache[8] || (_cache[8] = ($event: any) => (rewardObject.showOverlay = false))
        }, null, 8, ["model"])
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(_component_v_overlay, {
      modelValue: editRewardObject.showOverlay,
      "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((editRewardObject.showOverlay) = $event)),
      class: "align-center justify-center"
    }, {
      default: _withCtx(() => [
        _createVNode(DynamicForm, {
          title: "编辑暂存奖励",
          headers: ['奖励步数', '奖励原因'],
          model: editRewardObject.data,
          onSubmit: useSubmitEditReward,
          onClose: _cache[10] || (_cache[10] = ($event: any) => (editRewardObject.showOverlay = false))
        }, null, 8, ["model"])
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(_component_v_dialog, {
      modelValue: revokeConfirm.show,
      "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((revokeConfirm.show) = $event)),
      "max-width": "420px"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_card, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_card_title, { class: "text-h6" }, {
              default: _withCtx(() => _cache[28] || (_cache[28] = [
                _createTextVNode("确认撤销该事件结算？")
              ])),
              _: 1,
              __: [28]
            }),
            _createVNode(_component_v_card_text, null, {
              default: _withCtx(() => _cache[29] || (_cache[29] = [
                _createTextVNode(" 撤销后将回滚相关小组的领地与得分状态，请确认无误。"),
                _createElementVNode("br", null, null, -1),
                _createElementVNode("span", { class: "text-caption text-grey" }, "注意：仅当后续没有依赖该结算的新事件时才可撤销。", -1)
              ])),
              _: 1,
              __: [29]
            }),
            _createVNode(_component_v_card_actions, null, {
              default: _withCtx(() => [
                _createVNode(_component_v_spacer),
                _createVNode(_component_v_btn, {
                  color: "grey",
                  variant: "text",
                  onClick: _cache[12] || (_cache[12] = ($event: any) => (revokeConfirm.show = false))
                }, {
                  default: _withCtx(() => _cache[30] || (_cache[30] = [
                    _createTextVNode("取消")
                  ])),
                  _: 1,
                  __: [30]
                }),
                _createVNode(_component_v_btn, {
                  color: "error",
                  variant: "elevated",
                  onClick: doRevoke
                }, {
                  default: _withCtx(() => _cache[31] || (_cache[31] = [
                    _createTextVNode("确认撤销")
                  ])),
                  _: 1,
                  __: [31]
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"])
  ], 64))
}
}

})